import { useEffect, useMemo, useState } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useNavigate, useParams } from "react-router-dom";
import { Dropdown, Modal, Button } from "react-bootstrap";
import { format, differenceInCalendarDays } from "date-fns";
import { config, priceFormat } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const ContractSchedule = () => {
  const { id_schedule } = useParams();
  const navigate = useNavigate();
  const crmUser = JSON.parse(localStorage.getItem('crmUser'));
  const [schedule, setSchedule] = useState(null);
  const [contract, setContract] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [companyQuotations, setCompanyQuotations] = useState([]);
  const [isLoadingCompanyQuotations, setIsLoadingCompanyQuotations] = useState(false);
  const [insertLoadingId, setInsertLoadingId] = useState(null);

  useEffect(() => {
    if (id_schedule) {
      getSchedule();
    }
  }, [id_schedule]);

  const getSchedule = async () => {
    setIsLoading(true);
    try {
      const fetchSchedule = await jwtFetch(config.ENDPOINT + "get/contract_schedule", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_contract_schedule: id_schedule,
        }),
      });
      const responseSchedule = await fetchSchedule.json();
      if (responseSchedule.status === 200 && responseSchedule.data.length > 0) {
        const scheduleData = responseSchedule.data[0];
        setSchedule(scheduleData);
        await Promise.all([
          getContract(scheduleData.contract_id),
          getQuotations(scheduleData.id_contract_schedule),
        ]);
      } else {
        alert(responseSchedule.message || "Schedule not found");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getContract = async (contractId) => {
    try {
      if (!contractId) {
        setContract(null);
        return;
      }
      const fetchContract = await jwtFetch(config.ENDPOINT + "get/contract", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_contract: contractId,
        }),
      });
      const responseContract = await fetchContract.json();
      if (responseContract.status === 200 && responseContract.data.length > 0) {
        setContract(responseContract.data[0]);
      } else {
        setContract(null);
      }
    } catch (error) {
      console.error('Error in getContract:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getQuotations = async (scheduleId) => {
    try {
      if (!scheduleId) {
        setQuotations([]);
        return;
      }
      const fetchQuotations = await jwtFetch(config.ENDPOINT + "get/contract_schedule_quotations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contract_schedule_id: scheduleId,
        }),
      });
      const responseQuotations = await fetchQuotations.json();
      if (responseQuotations.status === 200) {
        setQuotations(responseQuotations.data || []);
      } else {
        setQuotations([]);
      }
    } catch (error) {
      console.error('Error in getQuotations:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const fetchCompanyQuotations = async (companyId) => {
    if (!companyId) {
      setCompanyQuotations([]);
      return;
    }
    setIsLoadingCompanyQuotations(true);
    try {
      const fetchQuotations = await jwtFetch(config.ENDPOINT + "get/quotation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ company_id: companyId }),
      });
      const responseQuotations = await fetchQuotations.json();
      if (responseQuotations.status === 200) {
        setCompanyQuotations(responseQuotations.data || []);
      } else {
        setCompanyQuotations([]);
      }
    } finally {
      setIsLoadingCompanyQuotations(false);
    }
  };

  const openSearchExistingModal = async () => {
    setSearchQuery('');
    setShowSearchModal(true);
    if (contract?.company_id) {
      await fetchCompanyQuotations(contract.company_id);
    }
  };

  const filteredCompanyQuotations = companyQuotations.filter((quotation) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return [
      quotation.quotation_code,
      quotation.subcompany_name,
      quotation.quotation_status,
      quotation.item_list || quotation.item_list_names || '',
    ]
      .filter(Boolean)
      .some((value) => value.toString().toLowerCase().includes(q));
  });

  const insertQuotationToSchedule = async (quotationId) => {
    if (!quotationId || !id_schedule) return;
    setInsertLoadingId(quotationId);
    try {
      const fetchInsert = await jwtFetch(config.ENDPOINT + "update/quotation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: quotationId,
          contract_schedule_id: parseInt(id_schedule, 10),
          updated_by: crmUser?.user_keyid || null,
          updated_on: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        }),
      });
      const responseInsert = await fetchInsert.json();
      if (responseInsert.status === 200) {
        setShowSearchModal(false);
        getQuotations(id_schedule);
      } else {
        alert(responseInsert.message || 'Unable to insert quotation to schedule');
      }
    } catch (error) {
      alert('Unable to insert quotation to schedule');
    } finally {
      setInsertLoadingId(null);
    }
  };

  const addQuotationUrl = useMemo(() => {
    return `/quotation/add?contract_shedule=${id_schedule}`;
  }, [id_schedule]);

  const getScheduleStatusLabel = (scheduleData) => {
    if (!scheduleData) {
      return "-";
    }
    if (scheduleData.schedule_status !== "Active") {
      return scheduleData.schedule_status;
    }
    if (!scheduleData.schedule_start_date) {
      return scheduleData.schedule_status;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(scheduleData.schedule_start_date);
    startDate.setHours(0, 0, 0, 0);
    const dayDiff = differenceInCalendarDays(startDate, today);
    const hasEndDate = !!scheduleData.schedule_end_date;
    const quotationCount = Number(
      quotations.length || scheduleData.quotation_count || scheduleData.quotations || 0
    );
    let endDayDiff = null;

    if (hasEndDate) {
      const endDate = new Date(scheduleData.schedule_end_date);
      endDate.setHours(0, 0, 0, 0);
      endDayDiff = differenceInCalendarDays(endDate, today);
    }

    if (dayDiff < 0 && hasEndDate && endDayDiff < 0) {
      return <span className="text-danger">Expired</span>;
    }
    if (dayDiff < 0) {
      if (quotationCount > 0) {
        return "Active";
      }
      return <span className="text-danger">Overdue</span>;
    }
    if (dayDiff === 0) {
      if (quotationCount > 0) {
        return "Active";
      }
      return <span className="text-danger">Due</span>;
    }
    if (dayDiff <= 7) {
      return <span className="text-warning">Upcoming</span>;
    }
    return "Not Started";
  };

  return (
    <div id="contract-schedule-detail">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard">
                <img className="h-100" src="/cuancuan.png" alt="PointStar" />
              </a>
            </div>
            <hr className="m-0" />
            <Menu active="clients" />
          </div>
          <div className="col-md-10">
            <Header />
            <hr className="m-0" />
            <div className="main">
              <div className="p-4">
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                      <div className="fs-24">
                        <button className="btn btn-link" onClick={() => navigate(-1)}>
                          <i className="bi bi-arrow-left" />
                        </button>{" "}
                        Contract Schedule Detail
                      </div>
                    </div>
                  </div>

                  {isLoading ? (
                    <div>Loading...</div>
                  ) : (
                    <div className="mb-4">
                      <div className="row mb-2">
                        <div className="col-md-2 text-secondary">Contract Name</div>
                        <div className="col-md-10">
                          {contract?.contract_name || "-"}
                          <a href={`/client/contract/detail/${contract?.id_contract}`}>
                            <i className="bi bi-link-45deg" />
                          </a>
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-2 text-secondary">Company Group</div>
                        <div className="col-md-10">{contract?.company_name || "-"}</div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-2 text-secondary">Subsidiary</div>
                        <div className="col-md-10">{contract?.subcompany_name || "-"}</div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-2 text-secondary">Start Date</div>
                        <div className="col-md-10">
                          {schedule?.schedule_start_date
                            ? format(new Date(schedule.schedule_start_date), "d MMM yyyy")
                            : "-"}
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-2 text-secondary">End Date</div>
                        <div className="col-md-8">
                          {schedule?.schedule_end_date
                            ? format(new Date(schedule.schedule_end_date), "d MMM yyyy")
                            : "-"}
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-2 text-secondary">Period</div>
                        <div className="col-md-8">
                          {schedule?.schedule_long_period
                            ? `${schedule.schedule_long_period} month(s)`
                            : "-"}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-2 text-secondary">Status</div>
                        <div className="col-md-8">{getScheduleStatusLabel(schedule)}</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="fs-24">Quotations</div>
                    <Dropdown>
                      <Dropdown.Toggle size="sm" variant="outline-secondary" id="quotation-actions-dropdown">
                        <i className="bi bi-plus-lg me-2" /> Add Quotation
                      </Dropdown.Toggle>

                      <Dropdown.Menu align="end">
                        <Dropdown.Item href={addQuotationUrl}>
                          <i className="bi bi-plus-lg me-2" /> Create New
                        </Dropdown.Item>
                        <Dropdown.Item onClick={openSearchExistingModal}>
                          <i className="bi bi-search me-2" /> Search Existing
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="bg-white border-radius-4 p-4">
                    {quotations.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover bordered mb-0">
                          <thead>
                            <tr>
                              <th className="bg-gold w-30">#</th>
                              <th className="bg-gold">Quotation Number</th>
                              <th className="bg-gold">Status</th>
                              <th className="bg-gold text-end">Total</th>
                              <th className="bg-gold w-90"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotations.map((quotation, index) => (
                              <tr key={quotation.id_quotation}>
                                <td align="center" valign="middle">
                                  {index + 1}
                                </td>
                                <td valign="middle">{quotation.quotation_code}</td>
                                <td valign="middle">{quotation.quotation_status}</td>
                                <td align="right" valign="middle">
                                  {priceFormat(quotation.quotation_total, quotation.quotation_currency)}
                                </td>
                                <td align="right" valign="middle">
                                  <a
                                    className="btn btn-sm btn-outline-secondary"
                                    href={`/quotation/detail/${quotation.id_quotation}`}
                                  >
                                    <i className="bi bi-eye" />
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="mb-0 text-secondary">
                        No quotations are linked to this schedule yet.
                      </p>
                    )}
                  </div>
                  <Modal show={showSearchModal} onHide={() => setShowSearchModal(false)} size="xl" centered>
                    <Modal.Header closeButton>
                      <Modal.Title>Search Existing Quotations</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <div className="mb-3">
                        <input
                          className="form-control form-control-lg"
                          placeholder="Search by quotation number, subsidiary, status, or item list..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead>
                            <tr>
                              <th className="bg-secondary text-light">#</th>
                              <th className="bg-secondary text-light">Quotation Number</th>
                              <th className="bg-secondary text-light">Status</th>
                              <th className="bg-secondary text-light">Subsidiary</th>
                              <th className="bg-secondary text-light text-end">Amount</th>
                              <th className="bg-secondary text-light text-center">Start Period</th>
                              <th className="bg-secondary text-light text-center">End Period</th>
                              <th className="bg-secondary text-light"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoadingCompanyQuotations ? (
                              <tr>
                                <td colSpan={8} className="text-center py-4">Loading...</td>
                              </tr>
                            ) : filteredCompanyQuotations.length ? (
                              filteredCompanyQuotations.map((quotation, index) => (
                                <tr key={quotation.id_quotation}>
                                  <td valign="middle">{index + 1}</td>
                                  <td valign="middle">{quotation.quotation_code}</td>
                                  <td valign="middle">{quotation.quotation_status}</td>
                                  <td valign="middle">{quotation.subcompany_name}</td>
                                  <td valign="middle" align="right">
                                    {priceFormat(quotation.quotation_total, quotation.quotation_currency)}
                                  </td>
                                  <td valign="middle" align="center">
                                    {quotation.iou_start_date
                                      ? format(new Date(quotation.iou_start_date), 'd MMM yyyy')
                                      : quotation.simulation_start_date
                                      ? format(new Date(quotation.simulation_start_date), 'd MMM yyyy')
                                      : '-'}
                                  </td>
                                  <td valign="middle" align="center">
                                    {quotation.iou_end_date
                                      ? format(new Date(quotation.iou_end_date), 'd MMM yyyy')
                                      : quotation.simulation_end_date
                                      ? format(new Date(quotation.simulation_end_date), 'd MMM yyyy')
                                      : '-'}
                                  </td>
                                  <td valign="middle" align="right">
                                    <button
                                      className="btn btn-sm btn-primary"
                                      type="button"
                                      onClick={() => insertQuotationToSchedule(quotation.id_quotation)}
                                      disabled={insertLoadingId === quotation.id_quotation}
                                    >
                                      {insertLoadingId === quotation.id_quotation ? 'Inserting...' : 'Insert'}
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={8} className="text-center py-4 text-secondary">
                                  No quotations found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button variant="outline-secondary" onClick={() => setShowSearchModal(false)}>
                        Close
                      </Button>
                    </Modal.Footer>
                  </Modal>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractSchedule;


